import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Recycle, Sparkles, Truck, ArrowRight, Package, Trash2, CheckCircle, Phone, Clock, Shield, Leaf } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1766040923580-16ad32fae8b4?auto=compress&cs=tinysrgb&w=1920';

const T = {
  heroLabel: { de: 'Umweltbewusst & Zuverlässig', en: 'Eco-friendly & Reliable' },
  heroTitle1: { de: 'Professionelle', en: 'Professional' },
  heroTitle2: { de: 'Entrümpelung', en: 'Clearance' },
  heroTitle3: { de: '& Entsorgung', en: '& Disposal' },
  heroSub: { de: 'Wir räumen, entsorgen und reinigen – schnell, sauber und umweltbewusst. Für Privat und Gewerbe.', en: 'We clear, dispose and clean – fast, clean and eco-friendly. For private and commercial clients.' },
  heroCta1: { de: 'Kostenlos anfragen', en: 'Free Enquiry' },
  heroCta2: { de: 'Leistungen', en: 'Services' },
  servicesLabel: { de: 'Leistungen', en: 'Services' },
  servicesTitle: { de: 'Was wir für Sie tun', en: 'What We Do for You' },
  allServices: { de: 'Alle Leistungen im Detail', en: 'All Services in Detail' },
  processLabel: { de: 'So funktioniert\'s', en: 'How It Works' },
  processTitle: { de: 'In 4 Schritten zur sauberen Lösung', en: '4 Steps to a Clean Solution' },
  ctaTitle: { de: 'Kostenlose Besichtigung anfragen', en: 'Request Free Inspection' },
  ctaSub: { de: 'Wir erstellen Ihnen ein unverbindliches Festpreis-Angebot vor Ort.', en: 'We provide you with a non-binding fixed-price quote on-site.' },
  ctaBtn: { de: 'Jetzt anfragen', en: 'Enquire Now' },
  ctaCall: { de: 'Oder direkt anrufen', en: 'Or call directly' },
};

const SERVICES_DATA = {
  de: [
    { icon: Home, title: 'Haushaltsauflösungen', desc: 'Komplette Räumung von Wohnungen und Häusern – besenrein und termingerecht.' },
    { icon: Trash2, title: 'Entrümpelung', desc: 'Gewerbliche und private Entrümpelung jeder Größenordnung.' },
    { icon: Recycle, title: 'Entsorgung & Recycling', desc: 'Fachgerechte und umweltbewusste Entsorgung aller Materialien.' },
    { icon: Sparkles, title: 'Reinigungsservice', desc: 'Professionelle Endreinigung nach der Räumung.' },
    { icon: Truck, title: 'Transport & Umzüge', desc: 'Sicherer Transport an den gewünschten Ort.' },
    { icon: Package, title: 'Wertanrechnung', desc: 'Verwertbare Gegenstände reduzieren Ihre Kosten.' },
  ],
  en: [
    { icon: Home, title: 'Household Clearances', desc: 'Complete clearance of flats and houses – broom-clean and on schedule.' },
    { icon: Trash2, title: 'Decluttering', desc: 'Commercial and private decluttering of any scale.' },
    { icon: Recycle, title: 'Disposal & Recycling', desc: 'Professional and eco-friendly disposal of all materials.' },
    { icon: Sparkles, title: 'Cleaning Service', desc: 'Professional final cleaning after clearance.' },
    { icon: Truck, title: 'Transport & Moving', desc: 'Safe transport to the desired location.' },
    { icon: Package, title: 'Value Credit', desc: 'Reusable items reduce your costs.' },
  ],
};

const STEPS = {
  de: [
    { num: '01', title: 'Kontaktaufnahme', desc: 'Sie rufen an oder schreiben uns – kostenlos und unverbindlich.' },
    { num: '02', title: 'Besichtigung', desc: 'Wir kommen vorbei und erstellen ein transparentes Festpreisangebot.' },
    { num: '03', title: 'Durchführung', desc: 'Professionelle Räumung zum vereinbarten Termin.' },
    { num: '04', title: 'Übergabe', desc: 'Besenreine Übergabe – sauber und schlüsselfertig.' },
  ],
  en: [
    { num: '01', title: 'Contact', desc: 'Call or write to us – free and non-binding.' },
    { num: '02', title: 'Inspection', desc: 'We visit and provide a transparent fixed-price quote.' },
    { num: '03', title: 'Execution', desc: 'Professional clearance at the agreed time.' },
    { num: '04', title: 'Handover', desc: 'Broom-clean handover – clean and ready to go.' },
  ],
};

export default function AtoBObjektserviceHome() {
  const { language } = useLanguage();
  const services = SERVICES_DATA[language];
  const steps = STEPS[language];
  return (
    <main data-testid="sub-atob-objektservice-home" className="bg-[#f8f7f5]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      
      {/* ===================== HERO ===================== */}
      <section className="relative min-h-[90vh] flex items-center pt-20">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="AtoB Objektservice" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/40" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20 w-full">
          <motion.div 
            initial={{ opacity: 0, y: 40 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.8 }}
            className="max-w-xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <Leaf size={20} className="text-[#F5A623]" />
              <span className="text-[#F5A623] text-sm font-semibold tracking-[0.15em] uppercase">{T.heroLabel[language]}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F1A2E] mb-6 leading-[1.1]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {T.heroTitle1[language]}<br />
              <span className="text-[#F5A623]">{T.heroTitle2[language]}</span><br />
              {T.heroTitle3[language]}
            </h1>
            
            <p className="text-lg text-[#52525b] leading-relaxed mb-10 max-w-lg">
              {T.heroSub[language]}
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/atob-objektservice/kontakt" 
                data-testid="hero-cta-kontakt"
                className="group bg-[#F5A623] text-white px-8 py-4 text-sm font-bold tracking-wider uppercase inline-flex items-center gap-3 hover:bg-[#0F1A2E] transition-all duration-300 rounded-lg shadow-lg shadow-[#F5A623]/20"
              >
                {T.heroCta1[language]}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a 
                href="tel:+4921732046452"
                className="border border-[#0F1A2E]/20 text-[#0F1A2E] px-8 py-4 text-sm font-semibold tracking-wider uppercase hover:bg-[#0F1A2E] hover:text-white transition-all inline-flex items-center gap-2 rounded-lg"
              >
                <Phone size={16} /> Anrufen
              </a>
            </div>
            
            {/* Trust badges */}
            <div className="flex flex-wrap gap-6 mt-12 pt-8 border-t border-[#0F1A2E]/10">
              {[
                { icon: Clock, label: 'Schnelle Termine' },
                { icon: Shield, label: 'Festpreis-Garantie' },
                { icon: Recycle, label: 'Öko-Entsorgung' },
              ].map((badge, i) => (
                <div key={i} className="flex items-center gap-2">
                  <badge.icon size={18} className="text-[#F5A623]" />
                  <span className="text-sm text-[#52525b] font-medium">{badge.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===================== SERVICES ===================== */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4 block">Was wir bieten</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F1A2E] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Unsere Leistungen
            </h2>
            <p className="text-lg text-[#52525b] max-w-2xl mx-auto">
              Kompletter Service aus einer Hand – von der Entrümpelung bis zur umweltgerechten Entsorgung
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="group bg-white p-8 rounded-2xl border border-[#e5e5e5] hover:border-[#F5A623]/30 shadow-[0_8px_32px_rgba(0,51,102,0.04)] hover:shadow-[0_16px_48px_rgba(0,51,102,0.08)] transition-all duration-300"
              >
                <div className="w-14 h-14 bg-[#F5A623]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#F5A623] transition-colors">
                  <service.icon size={28} className="text-[#F5A623] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-semibold text-[#0F1A2E] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{service.title}</h3>
                <p className="text-[#52525b] text-sm leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link 
              to="/atob-objektservice/leistungen"
              className="inline-flex items-center gap-2 text-[#F5A623] hover:text-[#0F1A2E] transition-colors group"
            >
              <span className="text-sm font-semibold tracking-wider uppercase">Alle Leistungen im Detail</span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ===================== PROCESS STEPS ===================== */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4 block">So läuft's ab</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F1A2E] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              In 4 Schritten zum Ziel
            </h2>
            <p className="text-lg text-[#52525b] max-w-xl mx-auto">
              Einfach, transparent und stressfrei
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="relative"
              >
                {/* Connector line */}
                {i < STEPS.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-1/2 w-full h-[2px] bg-gradient-to-r from-[#F5A623]/50 to-[#F5A623]/20" />
                )}
                
                <div className="relative bg-[#f8f7f5] p-8 rounded-2xl text-center">
                  {/* Number circle */}
                  <div className="w-16 h-16 bg-[#F5A623] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#F5A623]/30">
                    <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.num}</span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-[#0F1A2E] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h3>
                  <p className="text-sm text-[#52525b] leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== ABOUT ===================== */}
      <section className="py-24 md:py-32 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4 block">Über uns</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F1A2E] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Ihr Partner für professionelle Entrümpelung
              </h2>
              <p className="text-[#52525b] leading-relaxed mb-6">
                AtoB Objektservice GmbH steht für professionelle und umweltbewusste Entrümpelung und Entsorgung. Ob Haushaltsauflösung, gewerbliche Räumung oder Reinigungsservice – wir übernehmen alle Arbeiten schnell, sauber und zuverlässig.
              </p>
              <p className="text-[#52525b] leading-relaxed mb-8">
                Dabei legen wir besonderen Wert auf fachgerechte Entsorgung und maximale Recycling-Quoten. Als Teil der Basic United Holding GmbH profitieren wir von einem starken Netzwerk.
              </p>
              
              {/* Benefits list */}
              <div className="space-y-4">
                {[
                  'Festpreis-Garantie ohne versteckte Kosten',
                  'Umweltbewusste Entsorgung mit hoher Recycling-Quote',
                  'Kurzfristige Termine möglich',
                  'Besenreine Übergabe garantiert',
                ].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-[#F5A623] flex-shrink-0" />
                    <span className="text-[#0F1A2E] font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {/* Info card */}
              <div className="bg-white p-10 rounded-2xl shadow-xl shadow-[#0F1A2E]/5 border border-[#e5e5e5]">
                <div className="flex items-center gap-4 mb-8 pb-8 border-b border-[#e5e5e5]">
                  <div className="w-16 h-16 bg-[#0F1A2E] rounded-xl flex items-center justify-center">
                    <Home size={32} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0F1A2E]" style={{ fontFamily: 'Outfit, sans-serif' }}>AtoB Objektservice</h3>
                    <p className="text-[#52525b]">Teil der Basic United Holding</p>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <p className="text-xs text-[#52525b] uppercase tracking-wider mb-1">Geschäftsführung</p>
                    <p className="font-semibold text-[#0F1A2E]">Admir Basic & Alessandro Costantino</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#52525b] uppercase tracking-wider mb-1">Einsatzgebiet</p>
                    <p className="font-semibold text-[#0F1A2E]">Nordrhein-Westfalen & Umgebung</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#52525b] uppercase tracking-wider mb-1">Recycling-Quote</p>
                    <p className="font-semibold text-[#F5A623] text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>90%+</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===================== CTA ===================== */}
      <section className="py-24 md:py-32 bg-[#0F1A2E]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-20 h-20 bg-[#F5A623] rounded-full flex items-center justify-center mx-auto mb-8">
              <Home size={40} className="text-white" />
            </div>
            
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Entrümpelung nötig?
            </h2>
            <p className="text-lg text-white/70 mb-10 max-w-xl mx-auto">
              Kontaktieren Sie uns für eine kostenlose und unverbindliche Besichtigung. Wir erstellen Ihnen ein faires Festpreis-Angebot.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/atob-objektservice/kontakt"
                data-testid="cta-kontakt"
                className="inline-flex items-center gap-3 bg-[#F5A623] text-white px-10 py-5 text-sm font-bold tracking-wider uppercase hover:bg-white hover:text-[#0F1A2E] transition-all duration-300 rounded-lg"
              >
                Kostenlose Besichtigung <ArrowRight size={18} />
              </Link>
              <a 
                href="tel:+4921732046452"
                className="inline-flex items-center gap-3 bg-white/10 text-white px-10 py-5 text-sm font-bold tracking-wider uppercase hover:bg-white hover:text-[#0F1A2E] transition-all duration-300 rounded-lg border border-white/20"
              >
                <Phone size={18} /> Jetzt anrufen
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
